import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from "@tanstack/react-query";

import { fetchJson } from "@/lib/api";

import type {
  AddressRow,
  CoiOnFilePayload,
  DashboardPayload,
  ExtensionConfirmResponse,
  ExtensionPreviewPayload,
  IssueSubmitPayload,
  OrderDetailPayload,
  PaymentMethodRow,
  ProfilePayload,
  ReadyForPickupResponse,
  ReorderResponse
} from "./types";

// Phase 4a customer-portal — query + mutation hooks. Every read
// passes through fetchJson which auto-injects the CSRF token and
// converts non-2xx into ApiError, so 401 surfaces as `e.status===401`
// and the UI can redirect to /sign-in with the return_to param.

const DASHBOARD_KEY = ["portal", "dashboard"] as const;
const ORDER_KEY = (n: string) => ["portal", "order", n] as const;
const PROFILE_KEY = ["portal", "profile"] as const;
const ADDRESSES_KEY = ["portal", "addresses"] as const;
const PAYMENT_KEY = ["portal", "payment_methods"] as const;
const COI_KEY = ["portal", "coi"] as const;
const ORDER_TICKETS_KEY = (n: string) => ["portal", "order", n, "issues"] as const;

export function useDashboard(): UseQueryResult<DashboardPayload> {
  return useQuery({
    queryKey: DASHBOARD_KEY,
    queryFn: async () => fetchJson<DashboardPayload>("/portal/dashboard.json")
  });
}

export function useOrderDetail(orderNumber: string): UseQueryResult<OrderDetailPayload> {
  return useQuery({
    queryKey: ORDER_KEY(orderNumber),
    queryFn: async () => fetchJson<OrderDetailPayload>(`/portal/orders/${orderNumber}.json`),
    enabled: orderNumber.length > 0
  });
}

export function useReadyForPickup(
  orderNumber: string
): UseMutationResult<ReadyForPickupResponse, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      fetchJson<ReadyForPickupResponse>(
        `/portal/orders/${orderNumber}/ready_for_pickup.json`,
        { method: "POST" }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDER_KEY(orderNumber) });
      qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
    }
  });
}

export function useReorder(
  orderNumber: string
): UseMutationResult<ReorderResponse, Error, { replace?: boolean } | undefined> {
  return useMutation({
    mutationFn: async (vars) => {
      const replace = vars?.replace ? "?replace=true" : "";
      return fetchJson<ReorderResponse>(`/portal/orders/${orderNumber}/reorder.json${replace}`, {
        method: "POST"
      });
    }
  });
}

export function useSubmitIssue(
  orderNumber: string
): UseMutationResult<{ id: number }, Error, IssueSubmitPayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      fetchJson<{ id: number }>(`/portal/orders/${orderNumber}/issues.json`, {
        method: "POST",
        body: payload as unknown as Record<string, unknown>
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDER_KEY(orderNumber) });
      qc.invalidateQueries({ queryKey: ORDER_TICKETS_KEY(orderNumber) });
    }
  });
}

export function useProfile(): UseQueryResult<ProfilePayload> {
  return useQuery({
    queryKey: PROFILE_KEY,
    queryFn: async () => fetchJson<ProfilePayload>("/portal/profile.json")
  });
}

export function useUpdateProfile(): UseMutationResult<ProfilePayload, Error, Partial<ProfilePayload>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) =>
      fetchJson<ProfilePayload>("/portal/profile.json", {
        method: "PATCH",
        body: patch as unknown as Record<string, unknown>
      }),
    onSuccess: (data) => qc.setQueryData(PROFILE_KEY, data)
  });
}

export function useAddresses(): UseQueryResult<{ rows: AddressRow[] }> {
  return useQuery({
    queryKey: ADDRESSES_KEY,
    queryFn: async () => fetchJson<{ rows: AddressRow[] }>("/portal/addresses.json")
  });
}

interface AddressInput {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  label?: string | null;
  country?: string;
  default_delivery?: boolean;
}

export function useCreateAddress(): UseMutationResult<AddressRow, Error, AddressInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      fetchJson<AddressRow>("/portal/addresses.json", {
        method: "POST",
        body: input as unknown as Record<string, unknown>
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADDRESSES_KEY })
  });
}

export function useDeleteAddress(): UseMutationResult<null, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      fetchJson<null>(`/portal/addresses/${id}.json`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADDRESSES_KEY })
  });
}

export function usePaymentMethods(): UseQueryResult<{ rows: PaymentMethodRow[] }> {
  return useQuery({
    queryKey: PAYMENT_KEY,
    queryFn: async () => fetchJson<{ rows: PaymentMethodRow[] }>("/portal/payment_methods.json")
  });
}

export function useSetDefaultPayment(): UseMutationResult<PaymentMethodRow, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      fetchJson<PaymentMethodRow>(`/portal/payment_methods/${id}.json`, {
        method: "PATCH",
        body: { default: true }
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PAYMENT_KEY })
  });
}

export function useRemovePayment(): UseMutationResult<null, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      fetchJson<null>(`/portal/payment_methods/${id}.json`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PAYMENT_KEY })
  });
}

export function useCoiOnFile(): UseQueryResult<CoiOnFilePayload> {
  return useQuery({
    queryKey: COI_KEY,
    queryFn: async () => fetchJson<CoiOnFilePayload>("/portal/coi.json")
  });
}

// Phase 4b extension-requests — preview + confirm hooks. Preview is
// a *mutation* (not a query) because the customer drives the date
// picker; React-Query's idempotent caching for queries would
// confuse rapid date changes more than help.
export function usePreviewExtension(
  orderNumber: string
): UseMutationResult<ExtensionPreviewPayload, Error, { new_return_date: string }> {
  return useMutation({
    mutationFn: async (vars) =>
      fetchJson<ExtensionPreviewPayload>(
        `/portal/orders/${orderNumber}/extensions/preview.json`,
        { method: "POST", body: vars as unknown as Record<string, unknown> }
      )
  });
}

export function useConfirmExtension(
  orderNumber: string
): UseMutationResult<
  ExtensionConfirmResponse,
  Error,
  { new_return_date: string; alternative_unit_id?: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars) =>
      fetchJson<ExtensionConfirmResponse>(`/portal/orders/${orderNumber}/extensions.json`, {
        method: "POST",
        body: vars as unknown as Record<string, unknown>
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDER_KEY(orderNumber) });
      qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
    }
  });
}

// Phase 4c availability-procurement (FR-5). Customer accept / reject
// the admin's proposed window on a procurement_pending order. Both
// mutations invalidate the order detail query so the proposed-change
// banner disappears on success.
export interface ProposedWindowResponse {
  intent_id: number;
  intent_status: string;
  order_status: string | null;
}

export function useAcceptProposedWindow(
  orderNumber: string
): UseMutationResult<ProposedWindowResponse, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      fetchJson<ProposedWindowResponse>(
        `/portal/orders/${orderNumber}/accept_proposed_window.json`, { method: "POST" }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDER_KEY(orderNumber) });
      qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
    }
  });
}

export function useRejectProposedWindow(
  orderNumber: string
): UseMutationResult<ProposedWindowResponse, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      fetchJson<ProposedWindowResponse>(
        `/portal/orders/${orderNumber}/reject_proposed_window.json`, { method: "POST" }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDER_KEY(orderNumber) });
      qc.invalidateQueries({ queryKey: DASHBOARD_KEY });
    }
  });
}

// Phase 5c portal-dispute-button — file a §11 invoice dispute from the
// customer billing page. The endpoint already exists from Phase 4f;
// this hook is the customer-facing entry point that was missing.
export interface DisputeOpenInput {
  reason: string;
  explanation: string;
}

export interface DisputeOpenResponse {
  id: number;
  reason: string;
  status: string;
  explanation: string;
  filed_at: string;
}

// Shared with BillingPage.tsx so a mutation that touches billing data
// (pay, file-dispute) invalidates the same React-Query cache key the
// page reads from. Two arrays compare structurally in React Query, but
// keeping one source of truth removes the chance of drift.
export const BILLING_KEY = ["portal", "billing"] as const;

export function useFileDispute(
  invoiceNumber: string
): UseMutationResult<DisputeOpenResponse, Error, DisputeOpenInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars) =>
      fetchJson<DisputeOpenResponse>(`/portal/invoices/${invoiceNumber}/dispute.json`, {
        method: "POST",
        body: { dispute: vars } as unknown as Record<string, unknown>
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BILLING_KEY });
    }
  });
}
